import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
  type RefObject,
} from "react"
import {
  AlertDialog,
  AlertDialogLabel,
  AlertDialogDescription,
} from "@reach/alert-dialog"

const ModalContext = createContext<{
  showDialog: boolean
  setShowDialog: (value: boolean) => void
  focusRef: RefObject<HTMLElement | null>
  hasFocusRefOverride: boolean
}>({
  showDialog: false,
  setShowDialog: () => {},
  focusRef: { current: null },
  hasFocusRefOverride: false,
})

export function Modal({
  children,
  onOpenFocusRef,
}: {
  children: ReactNode
  onOpenFocusRef?: RefObject<HTMLElement | null>
}) {
  const hasFocusRefOverride = Boolean(onOpenFocusRef)

  const defaultFocusRef = useRef<HTMLElement>(null)
  const focusRef = hasFocusRefOverride ? onOpenFocusRef! : defaultFocusRef

  const [showDialog, setShowDialog] = useState(false)

  return (
    <ModalContext.Provider
      value={{ showDialog, setShowDialog, focusRef, hasFocusRefOverride }}
    >
      {children}
    </ModalContext.Provider>
  )
}

Modal.Trigger = ModalTrigger
Modal.Show = ModalShow
Modal.Header = ModalHeader
Modal.Body = ModalBody
Modal.Footer = ModalFooter
Modal.Close = ModalClose

export function useCloseModal() {
  const { setShowDialog } = useContext(ModalContext)

  return useCallback(() => setShowDialog(false), [setShowDialog])
}

export function useOpenModal() {
  const { setShowDialog } = useContext(ModalContext)

  return useCallback(() => setShowDialog(true), [setShowDialog])
}

export function ModalTrigger({
  as: Comp = "button",
  className = "btn",
  style = {},
  dataAttributes = {},
  children,
}: {
  as?: ElementType
  className?: string
  style?: CSSProperties
  dataAttributes?: Record<string, string>
  children?: ReactNode
}) {
  const { setShowDialog } = useContext(ModalContext)
  return (
    <Comp
      className={className}
      style={style}
      onClick={() => setShowDialog(true)}
      {...dataAttributes}
    >
      {children}
    </Comp>
  )
}

export function ModalShow({
  children,
  onOpen,
  onClose = () => {},
}: {
  children: ReactNode
  onOpen?: () => void
  onClose?: () => void
}) {
  const { focusRef, showDialog, setShowDialog } = useContext(ModalContext)

  useEffect(() => {
    if (showDialog) {
      onOpen && onOpen()
    }
  }, [showDialog, onOpen])

  const handleDismiss = () => {
    setShowDialog(false)
    onClose()
  }

  return (
    showDialog && (
      <AlertDialog
        leastDestructiveRef={focusRef as RefObject<HTMLElement>}
        onDismiss={handleDismiss}
      >
        {children}
      </AlertDialog>
    )
  )
}

export function ModalHeader({ children }: { children?: ReactNode }) {
  return <AlertDialogLabel className="mb-1">{children}</AlertDialogLabel>
}

export function ModalBody({ children }: { children?: ReactNode }) {
  return <AlertDialogDescription>{children}</AlertDialogDescription>
}

export function ModalFooter({
  children,
  className = "",
}: {
  children?: ReactNode
  className?: string
}) {
  return <div className={`d-f jc-fe mt-3 ${className}`}>{children}</div>
}

export function ModalClose({
  as: Comp = "button",
  className = "btn text-btn mr-2",
  children,
  onClose = () => {},
}: {
  as?: ElementType
  className?: string
  children?: ReactNode
  onClose?: () => void
}) {
  const { hasFocusRefOverride, focusRef, setShowDialog } =
    useContext(ModalContext)

  const handleClose = () => {
    setShowDialog(false)
    onClose()
  }

  const refAttribute = hasFocusRefOverride ? null : { ref: focusRef }

  return (
    <Comp className={className} onClick={handleClose} {...refAttribute}>
      {children}
    </Comp>
  )
}
